<script setup>
import { computed, nextTick, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import AiAgentSuggestionsAPI from 'dashboard/api/aiAgentSuggestions';
import { useAlert } from 'dashboard/composables';
import { resolveAiAgentMode } from 'dashboard/helper/aiAgentMode';
import { normalizeAiResponseText } from 'dashboard/helper/aiResponseFormatter';
import {
  clampSuggestionText,
  suggestionDisplayText,
  suggestionNeedsExpand,
} from 'dashboard/helper/aiSuggestionLimits';
import { buildSuggestionMeta } from 'dashboard/helper/captainSuggestionTelemetry';
import BrasaMascot from 'dashboard/components-next/onboarding/BrasaMascot.vue';
import AssistChips from './AssistChips.vue';
import AiReportModal from './AiReportModal.vue';
import ConversationAiAgentAPI from 'dashboard/api/conversationAiAgent';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// SiteUp: sugestão minimal — texto + Enviar/Editar; Descartar/Reportar como
// ícones diretos (1 clique). Em suggest_auto busca sozinho; em suggest_manual
// mostra "Gerar resposta".
const props = defineProps({
  conversation: { type: Object, required: true },
  // Rascunho atual do editor (ReplyBox.message). Usado só para NÃO empurrar uma
  // sugestão automática por cima do que o atendente já está digitando.
  draft: { type: String, default: '' },
});

const emit = defineEmits(['use', 'send', 'shown']);
const { t } = useI18n();

const suggestion = ref('');
// Cartões de Apoio: chips que vêm no mesmo envelope da sugestão.
const tips = ref([]);
const cards = ref([]);
const loading = ref(false);
const expanded = ref(false);
const reporting = ref(false);
// Texto que estava na tela quando o atendente abriu o report.
const reportText = ref('');

const settings = computed(
  () => props.conversation?.additional_attributes?.ai_agent || {}
);

// Modo da IA desta conversa: vem FRESCO do backend (conversation.ai_agent_mode,
// config POR CAIXA). Mantém compat com override legado por-conversa.
const currentMode = computed(() => resolveAiAgentMode(props.conversation));

const notPaused = computed(() => settings.value.paused !== true);
// Auto suggestions: suggest_auto always; autonomous_suggest even when human
// reply paused autonomous sending (suggestions continue, auto-send does not).
const isSuggestAuto = computed(
  () =>
    currentMode.value === 'suggest_auto' ||
    currentMode.value === 'autonomous_suggest'
);
const isSuggestManual = computed(
  () => currentMode.value === 'suggest_manual' && notPaused.value
);
const showsSuggestionCard = computed(
  () => isSuggestAuto.value || isSuggestManual.value
);

const conversationId = computed(() => props.conversation?.id);
const lastMessage = computed(
  () => props.conversation?.last_non_activity_message
);
// message_type 0 = incoming (mensagem do lead)
const lastIncomingId = computed(() =>
  lastMessage.value?.message_type === 0 ? lastMessage.value?.id : null
);

// O atendente já está redigindo algo? Se tem rascunho com conteúdo (descontando
// espaços/tags vazias), NÃO auto-geramos por cima — só atrapalharia a digitação.
const agentIsComposing = computed(
  () => (props.draft || '').replace(/<[^>]*>/g, '').trim().length > 0
);

const POLL_INTERVAL_MS = 1500;
// Teto do poll (~2 min). Com a fila da IA parada o servidor responde
// "processing" para sempre e o card pedia sugestão a cada 1,5s sem parar.
// Estourou: sai do carregando e oferece gerar de novo.
const MAX_POLL_ATTEMPTS = 80;
const pollTimedOut = ref(false);
let pollTimer = null;
let requestVersion = 0;

const clearPoll = () => {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
};

// The component is reused while navigating between conversations. Invalidate
// both pending HTTP responses and polls so an older suggestion cannot land on a
// newer customer message in the same conversation.
const resetSuggestionState = () => {
  requestVersion += 1;
  clearPoll();
  suggestion.value = '';
  tips.value = [];
  cards.value = [];
  loading.value = false;
  pollTimedOut.value = false;
  expanded.value = false;
  reporting.value = false;
};

const previewText = computed(() =>
  suggestionDisplayText(suggestion.value, expanded.value)
);
const canToggleExpand = computed(() => suggestionNeedsExpand(suggestion.value));

watch(conversationId, resetSuggestionState);
watch(isSuggestAuto, enabled => {
  if (!enabled) resetSuggestionState();
});

const fetchSuggestion = async (manual = false, attempt = 0) => {
  if (!conversationId.value) return;

  const requestId = conversationId.value;
  const requestMessageId = lastIncomingId.value;
  const requestMode = currentMode.value;
  requestVersion += 1;
  const version = requestVersion;
  const requestIsCurrent = () =>
    version === requestVersion &&
    requestId === conversationId.value &&
    requestMessageId === lastIncomingId.value &&
    requestMode === currentMode.value;

  clearPoll();
  loading.value = true;
  pollTimedOut.value = false;
  suggestion.value = '';
  tips.value = [];
  cards.value = [];
  // O report guarda o próprio texto: uma sugestão nova não fecha o modal aberto.
  let keepLoading = false;

  try {
    const { data } = await AiAgentSuggestionsAPI.suggest(conversationId.value);
    if (!requestIsCurrent()) return;

    if (data?.status === 'processing') {
      if (attempt + 1 >= MAX_POLL_ATTEMPTS) {
        pollTimedOut.value = true;
        if (manual) useAlert(t('CONVERSATION.AI_SUGGESTION.ERROR'));
        return;
      }
      keepLoading = true;
      pollTimer = setTimeout(() => {
        pollTimer = null;
        if (requestIsCurrent()) fetchSuggestion(manual, attempt + 1);
      }, POLL_INTERVAL_MS);
      return;
    }

    if (data?.suggestion) {
      suggestion.value = clampSuggestionText(
        normalizeAiResponseText(data.suggestion)
      );
      tips.value = Array.isArray(data.tips) ? data.tips : [];
      cards.value = Array.isArray(data.cards) ? data.cards : [];
      expanded.value = false;
      const meta = buildSuggestionMeta(suggestion.value, props.conversation);
      if (meta) emit('shown', meta);
    } else if (manual) {
      useAlert(t('CONVERSATION.AI_SUGGESTION.EMPTY'));
    }
  } catch (error) {
    // Auto (proativo): falha em silêncio. Manual (clique): avisa o atendente
    // com o motivo certo (rede vs servidor) — nada de "motor de IA" genérico.
    if (manual && requestIsCurrent()) {
      const data = error?.response?.data;
      useAlert(
        data?.error ||
          (error?.response
            ? t('CONVERSATION.AI_SUGGESTION.ERROR')
            : t('CONVERSATION.AI_SUGGESTION.NETWORK_ERROR'))
      );
    }
  } finally {
    if (requestIsCurrent() && !keepLoading) {
      loading.value = false;
    }
  }
};

// Sugestão AUTOMÁTICA: gera sozinho quando chega mensagem do lead, mas com
// salvaguardas pra reduzir custo de LLM e atrito:
//  - debounce: rajada de mensagens seguidas do lead coalesce numa única geração;
//  - não auto-gera se o atendente já está com rascunho no editor (composing);
//  - re-checa as condições no momento de disparar (o estado pode ter mudado
//    durante a espera do debounce — ex.: o atendente começou a digitar, ou o
//    componente foi desmontado ao trocar de conversa).
const AUTO_DEBOUNCE_MS = 800;
let destroyed = false;
const autoFetch = debounce(() => {
  if (!destroyed && isSuggestAuto.value && !agentIsComposing.value) {
    fetchSuggestion();
  }
}, AUTO_DEBOUNCE_MS);

// So mensagem NOVA DO CLIENTE invalida o rascunho. Sem o guarda do `id`, uma
// mensagem de SAIDA (a resposta da propria IA, uma nota, o atendente) zera o
// gatilho, o watcher entende que mudou e apaga a sugestao pronta na cara do
// atendente (achado do Lucas na v952; so aparecia no F5 antes da v951 passar a
// atualizar last_non_activity_message ao vivo).
watch(
  lastIncomingId,
  (id, oldId) => {
    if (id && id !== oldId) resetSuggestionState();
    if (id && id !== oldId && isSuggestAuto.value && !agentIsComposing.value) {
      autoFetch();
    }
  },
  { immediate: true }
);

// Se o atendente está no fim do fio, a bolha nova/expandida precisa entrar
// na vista — mesmo padrão do SCROLL_TO_MESSAGE das mensagens.
const followThreadIfNearBottom = () => {
  const panel = document.querySelector('.conversation-panel');
  if (!panel) return;
  const distanceFromBottom =
    panel.scrollHeight - panel.scrollTop - panel.clientHeight;
  if (distanceFromBottom > 240) return;
  nextTick(() => {
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
  });
};

watch([suggestion, expanded, loading], ([text, , isLoading]) => {
  if (text || isLoading) followThreadIfNearBottom();
});

// Sugestão da Central enviada pelo balão na mesma conversa (D50): ela já
// respondeu a mensagem do cliente que esta sugestão respondia. Tira daqui para
// o atendente não mandar duas respostas. Não gera sugestão nova: só mensagem
// nova do cliente gera (watcher acima).
const onCenterSuggestionSent = payload => {
  if (Number(payload?.conversationId) !== Number(conversationId.value)) return;
  resetSuggestionState();
};
emitter.on(BUS_EVENTS.AI_CENTER_SUGGESTION_SENT, onCenterSuggestionSent);

onBeforeUnmount(() => {
  destroyed = true;
  emitter.off(BUS_EVENTS.AI_CENTER_SUGGESTION_SENT, onCenterSuggestionSent);
  resetSuggestionState();
});

const clearSuggestion = () => {
  suggestion.value = '';
  tips.value = [];
  cards.value = [];
  reporting.value = false;
  expanded.value = false;
};

const suggestionMeta = () =>
  buildSuggestionMeta(suggestion.value, props.conversation);

const useSuggestion = () => {
  emit('use', suggestion.value, suggestionMeta());
  clearSuggestion();
};

// SiteUp: "Enviar" — manda a sugestão direto. Não limpa o card aqui: se o
// canal rejeitar (ex.: comprimento), o atendente ainda vê e reusa o texto.
// O ReplyBox chama onSuccess só após envio bem-sucedido.
const sendSuggestion = () => {
  emit('send', suggestion.value, clearSuggestion, suggestionMeta());
};

const discard = () => {
  clearSuggestion();
};

const openPropertyModal = source => {
  emitter.emit(BUS_EVENTS.OPEN_PROPERTY_SUGGESTIONS, source);
};

// Mensagem do cliente que a sugestão respondia (a última de entrada).
const customerText = computed(() =>
  lastMessage.value?.message_type === 0 ? lastMessage.value?.content || '' : ''
);

// "Não é cliente" (grupo interno, fornecedor): desliga a IA nesta conversa e
// tira da Central. Fica no menu "..." (longe de Enviar/Editar) e o toast
// oferece Desfazer por 8s, que volta o modo que a conversa tinha.
const UNDO_WINDOW_MS = 8000;
const showMoreActions = ref(false);
const markingNotCustomer = ref(false);

const moreActionItems = computed(() => [
  {
    action: 'not_customer',
    value: 'not_customer',
    icon: 'i-ri-user-unfollow-line',
    label: t('CONVERSATION.AI_SUGGESTION.NOT_CUSTOMER'),
    disabled: markingNotCustomer.value,
  },
]);

// Desfaz a marca e devolve o modo gravado antes. Sem modo gravado a conversa
// seguia a caixa; modo em branco volta a herdar da caixa (legacy_ai_agent_mode).
const undoNotCustomer = async (targetConversationId, previousMode) => {
  try {
    await ConversationAiAgentAPI.update(targetConversationId, {
      not_customer: false,
      mode: previousMode || '',
    });
    useAlert(t('CONVERSATION.AI_SUGGESTION.NOT_CUSTOMER_UNDONE'));
  } catch (error) {
    useAlert(t('CONVERSATION.AI_SUGGESTION.NOT_CUSTOMER_UNDO_ERROR'));
  }
};

const markNotCustomer = async () => {
  if (markingNotCustomer.value) return;
  showMoreActions.value = false;
  markingNotCustomer.value = true;
  const targetConversationId = conversationId.value;
  const previousMode = settings.value.mode;
  try {
    await ConversationAiAgentAPI.update(targetConversationId, {
      not_customer: true,
    });
    clearSuggestion();
    useAlert(t('CONVERSATION.AI_SUGGESTION.NOT_CUSTOMER_DONE'), {
      type: 'button',
      message: t('CONVERSATION.AI_SUGGESTION.UNDO'),
      duration: UNDO_WINDOW_MS,
      callback: () => undoNotCustomer(targetConversationId, previousMode),
    });
  } catch (error) {
    useAlert(t('CONVERSATION.AI_SUGGESTION.NOT_CUSTOMER_ERROR'));
  } finally {
    markingNotCustomer.value = false;
  }
};

const onMoreAction = ({ action }) => {
  if (action === 'not_customer') markNotCustomer();
};

const openReport = () => {
  reportText.value = suggestion.value;
  reporting.value = true;
};
</script>

<template>
  <div class="contents">
    <div
      v-if="
        showsSuggestionCard &&
        (loading || suggestion || isSuggestManual || pollTimedOut)
      "
      class="ai-suggestion-bubble mb-3 flex w-full justify-end"
    >
      <div
        class="ml-auto w-fit max-w-[85%] md:max-w-[70%] rounded-[var(--workspace-radius,20px)] ltr:rounded-br-[5px] rtl:rounded-bl-[5px] border border-dashed border-n-teal-8 bg-n-teal-9/10 px-4 py-3"
      >
        <div
          class="mb-2 flex items-center gap-1.5 text-xs font-medium text-n-teal-11"
        >
          <span
            class="i-ri-sparkling-line size-3.5 shrink-0"
            aria-hidden="true"
          />
          <span>{{ t('CONVERSATION.AI_SUGGESTION.TITLE') }}</span>
        </div>

        <!-- Manual sem rascunho, ou poll que estourou o teto: gerar sob demanda -->
        <button
          v-if="(isSuggestManual || pollTimedOut) && !suggestion && !loading"
          type="button"
          data-testid="ai-suggestion-generate"
          class="inline-flex items-center gap-1 rounded-md bg-n-brand px-2.5 py-1 text-xs font-medium text-white hover:opacity-90"
          @click="fetchSuggestion(true)"
        >
          <span class="i-ri-sparkling-line size-3.5" />
          {{ t('CONVERSATION.AI_SUGGESTION.GENERATE') }}
        </button>

        <div
          v-else-if="loading"
          role="status"
          aria-live="polite"
          class="flex items-center gap-2 text-sm text-n-slate-11"
        >
          <!-- SITEUP V2 (F1.3): IA gerando sugestão → Thinking Orb -->
          <BrasaMascot state="inspect" :size="20" />
          {{ t('CONVERSATION.AI_SUGGESTION.LOADING') }}
        </div>

        <template v-else-if="suggestion">
          <div
            data-testid="ai-suggestion-preview"
            class="max-h-40 min-w-0 overflow-y-auto overflow-x-hidden"
            :class="{ 'max-h-72': expanded }"
          >
            <p
              class="m-0 break-words text-sm text-n-slate-12 whitespace-pre-wrap [overflow-wrap:anywhere]"
            >
              {{ previewText }}
            </p>
          </div>
          <button
            v-if="canToggleExpand"
            type="button"
            data-testid="ai-suggestion-expand-toggle"
            class="mt-1 rounded-md px-0 py-0.5 text-xs font-medium text-n-brand hover:underline"
            @click="expanded = !expanded"
          >
            {{
              expanded
                ? t('CONVERSATION.AI_SUGGESTION.COLLAPSE')
                : t('CONVERSATION.AI_SUGGESTION.EXPAND')
            }}
          </button>

          <!-- Só com cartões: o endpoint já devolve `tips` pra todo mundo hoje, e
               esta bolha nunca os mostrou. Sem `cards` a conta continua vendo a
               sugestão exatamente como via antes da fatia. -->
          <AssistChips
            v-if="cards.length"
            class="mt-2"
            :tips="tips"
            :cards="cards"
            :conversation-id="conversation.id"
            @property-modal="openPropertyModal"
          />

          <div class="mt-2 flex min-w-0 flex-wrap items-center gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-full bg-n-teal-9 px-3 py-1 text-xs font-medium text-white hover:opacity-90"
              @click="sendSuggestion"
            >
              <span class="i-ri-send-plane-line size-3.5" />
              {{ t('CONVERSATION.AI_SUGGESTION.SEND') }}
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-full border border-n-weak bg-transparent px-3 py-1 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2"
              @click="useSuggestion"
            >
              <span class="i-ri-pencil-line size-3.5" />
              {{ t('CONVERSATION.AI_SUGGESTION.EDIT') }}
            </button>
            <!-- Mesma pílula dos botões de texto: sem borda e menores, os dois
                 ícones liam como sujeira ao lado de Enviar/Editar (achado do
                 Lucas na v950). Tooltip embaixo, para não cobrir os chips que
                 ficam na linha de cima. -->
            <button
              v-tooltip.bottom="t('CONVERSATION.AI_SUGGESTION.DISCARD')"
              type="button"
              data-testid="ai-suggestion-discard"
              class="inline-flex items-center justify-center rounded-full border border-n-weak bg-transparent px-2.5 py-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              :aria-label="t('CONVERSATION.AI_SUGGESTION.DISCARD')"
              @click="discard"
            >
              <span class="i-ri-delete-bin-line size-4" aria-hidden="true" />
            </button>
            <button
              v-tooltip.bottom="t('CONVERSATION.AI_SUGGESTION.REPORT')"
              type="button"
              data-testid="ai-suggestion-report"
              class="inline-flex items-center justify-center rounded-full border border-n-weak bg-transparent px-2.5 py-1 text-n-ruby-11 hover:bg-n-ruby-9/10"
              :aria-label="t('CONVERSATION.AI_SUGGESTION.REPORT')"
              @click="openReport"
            >
              <span class="i-ri-flag-line size-4" aria-hidden="true" />
            </button>
            <!-- "Não é cliente" desliga a IA: fica atrás do "...", separado de
                 Enviar/Editar, para não sair num clique acidental. -->
            <div
              v-on-clickaway="() => (showMoreActions = false)"
              class="relative ltr:ml-auto rtl:mr-auto"
            >
              <button
                v-tooltip.bottom="t('CONVERSATION.AI_SUGGESTION.MORE_ACTIONS')"
                type="button"
                data-testid="ai-suggestion-more-actions"
                class="inline-flex items-center justify-center rounded-full border border-n-weak bg-transparent px-2.5 py-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                :aria-label="t('CONVERSATION.AI_SUGGESTION.MORE_ACTIONS')"
                :aria-expanded="showMoreActions"
                @click="showMoreActions = !showMoreActions"
              >
                <span class="i-ph-dots-three size-4" aria-hidden="true" />
              </button>
              <DropdownMenu
                v-if="showMoreActions"
                data-testid="ai-suggestion-more-menu"
                class="bottom-full mb-1 ltr:right-0 rtl:left-0"
                :menu-items="moreActionItems"
                @action="onMoreAction"
              >
                <template #footer>
                  <p
                    class="m-0 w-60 px-4 pb-3 text-xs text-n-slate-11 whitespace-normal"
                  >
                    {{ t('CONVERSATION.AI_SUGGESTION.NOT_CUSTOMER_HINT') }}
                  </p>
                </template>
              </DropdownMenu>
            </div>
          </div>
        </template>
      </div>
    </div>
    <AiReportModal
      v-if="reporting"
      :show="reporting"
      :conversation-id="conversationId"
      source="suggestion"
      :ai-text="reportText"
      :customer-text="customerText"
      @sent="clearSuggestion"
      @close="reporting = false"
    />
  </div>
</template>
